import { Placement, autoPlacement, autoUpdate, useFloating } from '@floating-ui/react';
import { Menu, Transition } from '@headlessui/react';
import classNames from 'classnames';
import React from 'react';
import toast from 'react-hot-toast';
import { AlertOrangeIcon, CheckIcon } from '../icons';
import { Button } from './Button';
import { Spinner } from './Spinner';
import { TooltipNew } from './TooltipNew';

export interface DropdownMenuItem<T = React.ReactNode | string> {
  icon?: string;
  label: T;
  onClick: () => void;
  disabled?: boolean;
  disabledTooltip?: string;
  value?: string;
  description?: JSX.Element;
  hoverRightButton?: React.ReactNode;
  hint?: string;
}

export interface DropdownMenuProps {
  items: DropdownMenuItem[];
  button: JSX.Element;
  className?: string;
  itemClassName?: string;
  containerClassName?: string;
  selectedValue?: string;
  placement?: Placement;
  loading?: boolean;
  disabled?: boolean;
  autoPlace?: boolean;
  iconItemsWithBorder?: boolean;
}

export const DropdownMenu = ({
  items,
  button,
  selectedValue,
  className,
  itemClassName,
  containerClassName,
  placement = 'left-start',
  disabled = false,
  loading = false,
  autoPlace = false,
  iconItemsWithBorder = true,
}: DropdownMenuProps) => {
  const { refs, floatingStyles } = useFloating({
    placement,
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    transform: false,
    middleware: autoPlace ? [autoPlacement({ alignment: 'start' })] : [],
  });

  return (
    <Menu
      as="div"
      className={classNames(
        'relative flex items-center select-none',
        { 'w-full': !containerClassName?.includes('w-') },
        containerClassName,
      )}
    >
      <Menu.Button
        as="div"
        className="cursor-pointer w-full flex items-center justify-center"
        onClick={e => e.stopPropagation()}
        ref={refs.setReference}
        disabled={loading || disabled}
      >
        {loading && <Spinner size="sm" className="absolute right-0 mr-2" />}
        {button}
      </Menu.Button>
      <Transition
        as={React.Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={classNames(
            'space-y-1 bg-white z-10',
            'rounded border border-gray-200 divide-y divide-gray-200',
            {
              'w-max': items.length === 1 && !className?.match(/(^|\s)w-\S*/),
              'w-full': items.length !== 1 && !className?.match(/(^|\s)w-\S*/),
              'py-[14px]': !className?.includes('py-'),
            },
            className,
          )}
          ref={refs.setFloating}
          style={floatingStyles}
        >
          {items.map(
            (
              {
                label,
                onClick,
                disabled,
                disabledTooltip,
                icon,
                description,
                value,
                hoverRightButton,
                hint,
              },
              index,
            ) => {
              const menuItem = (
                <Menu.Item>
                  {({ active, close }) => (
                    <button
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        if (disabled && hint) {
                          toast(
                            t => (
                              <span className="flex flex-col justify-center items-center gap-2 text-sm">
                                <span>
                                  <img src={AlertOrangeIcon} className="w-8 h-8" alt="alert" />
                                </span>
                                {hint}
                                <Button variant="plain" onClick={() => toast.dismiss(t.id)}>
                                  Dismiss
                                </Button>
                              </span>
                            ),
                            { duration: 10000, id: 'not_register' },
                          );
                          close();
                        }
                        if (onClick) {
                          onClick();
                          close();
                        }
                      }}
                      title={hint}
                      disabled={disabled}
                      className={classNames(
                        itemClassName,
                        'w-full flex flex-col items-start text-gray-900 text-left text-[14px] leading-[20px] capitalize group/button',
                        {
                          'px-4': !itemClassName?.includes('px-'),
                          'py-[6px]': !itemClassName?.includes('py-'),
                          'whitespace-nowrap': items.length === 1,
                          'bg-gray-900/[.12]': active || (selectedValue && selectedValue === value),
                          'text-gray-400': disabled,
                          'text-gray-900': !disabled,
                        },
                      )}
                    >
                      <div className="w-full flex items-center gap-1.5">
                        {icon && (
                          <img
                            src={icon}
                            alt="Loading..."
                            className={classNames('w-6 h-6 min-w-6 min-h-6 mr-1', {
                              border: iconItemsWithBorder,
                            })}
                          />
                        )}
                        <div className="font-[Inter] text-[14px] leading-[20px] font-medium w-full">
                          {label}
                        </div>
                        {selectedValue && selectedValue === value && (
                          <img src={CheckIcon} alt="active" />
                        )}
                        {hoverRightButton && (
                          <span
                            className="group-hover/button:opacity-100 opacity-0"
                            onClick={close}
                          >
                            {hoverRightButton}
                          </span>
                        )}
                      </div>
                      {description && description}
                    </button>
                  )}
                </Menu.Item>
              );
              return disabled && disabledTooltip ? (
                <div key={`${value}_${index}`} className="relative">
                  {menuItem}
                  <TooltipNew
                    text={disabledTooltip}
                    positionStrategy="fixed"
                    place="top"
                    className="absolute inset-0 cursor-not-allowed"
                  >
                    <span className="block h-full w-full" />
                  </TooltipNew>
                </div>
              ) : (
                <React.Fragment key={`${value}_${index}`}>{menuItem}</React.Fragment>
              );
            },
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};
